/**
 * usePortfolioData — data model for the Portfolio cockpit.
 *
 * Truthful by construction: rows are the user's REAL properties (from the
 * session), rendered instantly; commercial metrics (30-day revenue/pace,
 * occupancy, blended reputation, arrivals, 12-month revenue-by-property) merge
 * in from the portfolio-aggregate endpoint (/app/portfolio-summary). Anything
 * the endpoint can't provide renders as pending ("—"), never as sample data.
 * Open-action counts come from the real Tasks backlog.
 */

import { useEffect, useMemo, useState } from "react"
import { useAuth } from "@/lib/auth"
import { apiClient } from "@/lib/api"
import { useTasks } from "@/pages/tasks/useTasks"
import { propDotClass, propChartColor } from "@/pages/tasks/TasksPage"

export type PortfolioStatus = "attention" | "watch" | "good" | "neutral"

export interface PortfolioRow {
  id: string
  name: string
  address: string
  initial: string
  avatarClass: string
  chartColor: string
  pmsLabel: string | null
  coverImageUrl: string | null
  openActions: number
  urgentActions: number
  revenue30d: number | null
  paceDeltaPct: number | null
  occupancyPct: number | null
  reputation: number | null
  nextArrivalLabel: string | null
  status: PortfolioStatus
}

export interface PortfolioTotals {
  revenue30d: number | null
  revenueDeltaPct: number | null
  avgOccupancyPct: number | null
  avgReputation: number | null
  openActions: number
  urgentActions: number
}

export interface PortfolioArrival {
  propertyId: string
  propertyName: string
  guest: string
  day: string
  mon: string
  detail: string
  channel: string
}

export interface PortfolioMonthPoint {
  month: string
  [propertyId: string]: number | string
}

export interface PortfolioData {
  greetingName: string
  greeting: string
  dateLabel: string
  propertyCount: number
  rows: PortfolioRow[]
  totals: PortfolioTotals | null
  monthly: PortfolioMonthPoint[]
  arrivals: PortfolioArrival[]
  totalOpenActions: number
  loading: boolean
  /** true while commercial metrics are absent (endpoint pending/no data yet) */
  metricsPending: boolean
}

type SummaryProperty = NonNullable<Awaited<ReturnType<typeof apiClient.getPortfolioSummary>>["properties"]>[number]
type SummaryTotals = NonNullable<Awaited<ReturnType<typeof apiClient.getPortfolioSummary>>["totals"]>
type SummaryArrival = NonNullable<Awaited<ReturnType<typeof apiClient.getPortfolioSummary>>["arrivals"]>[number]

function pmsLabel(provider: string | undefined): string | null {
  const p = (provider || "").trim()
  if (!p) return null
  if (p === "demo") return "Demo"
  return p.charAt(0).toUpperCase() + p.slice(1)
}

function timeGreeting(hour: number): string {
  if (hour < 5) return "Good evening"
  if (hour < 12) return "Good morning"
  if (hour < 18) return "Good afternoon"
  return "Good evening"
}

function deriveStatus(m: { occupancyPct: number | null; reputation: number | null; urgentActions: number; openActions: number; hasMetrics: boolean }): PortfolioStatus {
  if (!m.hasMetrics) return "neutral"
  if (m.urgentActions > 0 || (m.occupancyPct != null && m.occupancyPct < 40)) return "attention"
  if (m.openActions > 0 || (m.occupancyPct != null && m.occupancyPct < 60) || (m.reputation != null && m.reputation < 75)) return "watch"
  return "good"
}

function arrivalLabel(a: SummaryProperty["next_arrival"]): string | null {
  if (!a?.arrival_date) return null
  const date = new Date(`${a.arrival_date}T12:00:00`)
  if (Number.isNaN(date.getTime())) return null
  const days = Math.round((date.getTime() - Date.now()) / 86400000)
  const name = a.guest_name || "Guest"
  if (days <= 0) return `${name} arrives today`
  if (days === 1) return `${name} arrives tomorrow`
  return `${name} arrives in ${days} days`
}

function lastTwelveMonths(): string[] {
  const out: string[] = []
  const now = new Date()
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`)
  }
  return out
}

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

export function usePortfolioData(): PortfolioData {
  const { user, properties } = useAuth()
  const { counts } = useTasks()
  const [summaryProps, setSummaryProps] = useState<SummaryProperty[] | null>(null)
  const [totalsRaw, setTotalsRaw] = useState<SummaryTotals | null>(null)
  const [arrivalsRaw, setArrivalsRaw] = useState<SummaryArrival[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    apiClient
      .getPortfolioSummary()
      .then((res) => {
        if (cancelled) return
        if (res.ok) {
          setSummaryProps(Array.isArray(res.properties) ? res.properties : [])
          setTotalsRaw(res.totals ?? null)
          setArrivalsRaw(Array.isArray(res.arrivals) ? res.arrivals : [])
        }
      })
      .catch(() => { /* metrics stay pending */ })
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  return useMemo(() => {
    const now = new Date()
    const firstName = user?.name?.trim().split(/\s+/)[0] || ""
    const summaryById = new Map((summaryProps || []).map((p) => [p.id, p]))
    const nameById = new Map(properties.map((p) => [p.id, p.name || "Property"]))

    const rows: PortfolioRow[] = properties.map((p) => {
      const s = summaryById.get(p.id)
      const revenue30d = s?.revenue_30d ?? null
      const occupancyPct = s?.occupancy_30d_pct ?? null
      const reputation = s?.reputation_score ?? null
      const openActions = s?.open_actions ?? 0
      const urgentActions = s?.urgent_actions ?? 0
      const hasMetrics = revenue30d != null || occupancyPct != null || reputation != null
      return {
        id: p.id,
        name: p.name || "Property",
        address: p.address || "",
        initial: (p.name || "P").charAt(0).toUpperCase(),
        avatarClass: propDotClass(p.id),
        chartColor: propChartColor(p.id),
        pmsLabel: pmsLabel(p.pms_provider),
        coverImageUrl: p.cover_image_url || null,
        openActions,
        urgentActions,
        revenue30d,
        paceDeltaPct: s?.pace_delta_pct ?? null,
        occupancyPct,
        reputation,
        nextArrivalLabel: arrivalLabel(s?.next_arrival ?? null),
        status: deriveStatus({ occupancyPct, reputation, urgentActions, openActions, hasMetrics }),
      }
    })

    const totals: PortfolioTotals | null = totalsRaw
      ? {
          revenue30d: totalsRaw.revenue_30d,
          revenueDeltaPct: totalsRaw.revenue_delta_pct,
          avgOccupancyPct: totalsRaw.avg_occupancy_pct,
          avgReputation: totalsRaw.avg_reputation,
          openActions: totalsRaw.open_actions,
          urgentActions: totalsRaw.urgent_actions,
        }
      : null

    const months = lastTwelveMonths()
    const anyMonthly = (summaryProps || []).some((p) => Object.keys(p.monthly_revenue || {}).length > 0)
    const monthly: PortfolioMonthPoint[] = anyMonthly
      ? months.map((m) => {
          const point: PortfolioMonthPoint = { month: MONTH_SHORT[Number(m.slice(5, 7)) - 1] || m }
          for (const p of summaryProps || []) point[p.id] = p.monthly_revenue?.[m] ?? 0
          return point
        })
      : []

    const arrivals: PortfolioArrival[] = arrivalsRaw.map((a) => {
      const d = new Date(`${a.arrival_date}T12:00:00`)
      return {
        propertyId: a.property_id,
        propertyName: nameById.get(a.property_id) || "Property",
        guest: a.guest_name || "Guest",
        day: String(d.getDate()),
        mon: MONTH_SHORT[d.getMonth()] || "",
        detail: [a.nights ? `${a.nights} night${a.nights === 1 ? "" : "s"}` : "", a.guests ? `${a.guests} guest${a.guests === 1 ? "" : "s"}` : ""].filter(Boolean).join(" · "),
        channel: a.channel || "",
      }
    })

    const metricsPending = !totals || totals.revenue30d == null

    return {
      greetingName: firstName,
      greeting: timeGreeting(now.getHours()),
      dateLabel: now.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" }),
      propertyCount: properties.length,
      rows,
      totals,
      monthly,
      arrivals,
      totalOpenActions: totals?.openActions ?? counts.todo,
      loading,
      metricsPending,
    }
  }, [user?.name, properties, summaryProps, totalsRaw, arrivalsRaw, counts.todo, loading])
}
